import {
  Box,
  TextField,
  Typography,
  IconButton,
  Button,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import type { ParameterDefinition } from '@/graph/domain/types';
import { UnitValueField, type UnitValue } from './UnitValueField';
import { DropdownField } from './DropdownField';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function hasValidUnits(units?: string[]): boolean {
  if (!units || units.length === 0) return false;
  return units.some((u) => u && u !== 'none' && u.trim() !== '');
}

/**
 * Validates and filters a string value based on the field type.
 * For 'text' type: rejects numeric characters
 * For 'number' type: rejects alphabetic characters (handled by HTML input type)
 */
function validateParameterInput(value: string, fieldType: string): string {
  if (fieldType === 'text') {
    // For text-only fields, remove all numeric digits
    return value.replace(/\d/g, '');
  }
  // For other types, return as-is (number type is handled by HTML input)
  return value;
}

/**
 * Validates a UnitValue object, filtering the amount based on field type
 */
function validateUnitValue(value: UnitValue, fieldType: string): UnitValue {
  if (fieldType === 'text') {
    // For text-only fields, filter digits from amount
    const amount = String(value.amount || '').replace(/\d/g, '');
    return { ...value, amount };
  }
  return value;
}

/**
 * Validates a range value to ensure it's within min/max bounds.
 * Returns the value if valid, or empty string if invalid.
 */
function validateRangeInput(
  value: string | number | null,
  min: number | null | undefined,
  max: number | null | undefined,
): string | number | null {
  if (value === null || value === '' || value === undefined) return '';
  
  const numValue = typeof value === 'string' ? Number(value) : value;
  
  // If not a valid number, reject
  if (!Number.isFinite(numValue)) return '';
  
  // Check bounds
  if (min !== null && min !== undefined && numValue < min) return '';
  if (max !== null && max !== undefined && numValue > max) return '';
  
  return numValue;
}

/** Returns the key used to store a child's value — mirrors Angular's `child.safeKey || child.parameterName`. */
function childKey(child: ParameterDefinition): string {
  return child.safeKey || child.parameterName;
}

function makeEmptyRowValue(children: ParameterDefinition[]): Record<string, unknown> {
  const row: Record<string, unknown> = {};
  for (const child of children) {
    const key = childKey(child);
    if (
      child.fieldType === 'units' ||
      (hasValidUnits(child.parameterTags) &&
        ['text', 'textNumber', 'number'].includes(child.fieldType))
    ) {
      row[key] = { amount: '', unit: child.parameterTags?.[0] ?? '' };
    } else {
      row[key] = '';
    }
  }
  return row;
}

function unwrapValueWrapper(raw: unknown): unknown {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return raw;
  const obj = raw as Record<string, unknown>;
  const isParameterWrapper = ('parameterId' in obj || 'parameterName' in obj) && 'value' in obj;
  if (isParameterWrapper) return obj.value;
  return raw;
}

function extractPrimitive(raw: unknown): string | number {
  const unwrapped = unwrapValueWrapper(raw);
  if (unwrapped == null) return '';
  if (typeof unwrapped === 'string' || typeof unwrapped === 'number') return unwrapped;
  if (typeof unwrapped === 'object') {
    const obj = unwrapped as Record<string, unknown>;
    if (obj.label != null && obj.label !== '') return String(obj.label);
    if (obj.value != null && obj.value !== '') return extractPrimitive(obj.value);
    if (obj.amount != null && obj.amount !== '') return extractPrimitive(obj.amount);
    if (obj.input != null && obj.input !== '') return extractPrimitive(obj.input);
  }
  return '';
}

function toUnitValue(raw: unknown, fallbackUnit: string): UnitValue {
  const unwrapped = unwrapValueWrapper(raw);
  if (unwrapped && typeof unwrapped === 'object') {
    const obj = unwrapped as Record<string, unknown>;
    const nested =
      (obj.amount && typeof obj.amount === 'object' ? (obj.amount as Record<string, unknown>) : null) ??
      (obj.value && typeof obj.value === 'object' ? (obj.value as Record<string, unknown>) : null);
    if (nested) {
      return {
        amount: (nested.amount ?? nested.value ?? '') as string | number | null,
        unit: String(nested.unit ?? nested.units ?? obj.unit ?? obj.units ?? fallbackUnit),
      };
    }
    return {
      amount: extractPrimitive(obj.amount ?? obj.value ?? '') as string | number,
      unit: String(obj.unit ?? obj.units ?? fallbackUnit),
    };
  }
  return {
    amount: extractPrimitive(unwrapped) as string | number,
    unit: fallbackUnit,
  };
}

function toRangeValue(raw: unknown): number | string {
  const unwrapped = unwrapValueWrapper(raw);
  const primitive = extractPrimitive(unwrapped);
  if (primitive === '' || primitive == null) return '';
  const n = typeof primitive === 'number' ? primitive : Number(primitive);
  return Number.isFinite(n) ? n : '';
}

function isEmptyScalar(raw: unknown): boolean {
  const v = unwrapValueWrapper(raw);
  if (v == null) return true;
  if (typeof v === 'string') return v.trim() === '';
  if (typeof v === 'number') return !Number.isFinite(v);
  if (Array.isArray(v)) return v.length === 0;
  return false;
}

function readUnitAndAmount(raw: unknown): { amount: unknown; unit: string } {
  const value = unwrapValueWrapper(raw);

  if (value == null) return { amount: '', unit: '' };
  if (typeof value === 'string' || typeof value === 'number') return { amount: value, unit: '' };
  if (typeof value !== 'object' || Array.isArray(value)) return { amount: '', unit: '' };

  const obj = value as Record<string, unknown>;
  const nested =
    (obj.amount && typeof obj.amount === 'object' && !Array.isArray(obj.amount)
      ? (obj.amount as Record<string, unknown>)
      : null) ??
    (obj.value && typeof obj.value === 'object' && !Array.isArray(obj.value)
      ? (obj.value as Record<string, unknown>)
      : null);

  if (nested) {
    return {
      amount: nested.amount ?? nested.value ?? '',
      unit: String(nested.unit ?? nested.units ?? obj.unit ?? obj.units ?? '').trim(),
    };
  }

  return {
    amount: obj.amount ?? obj.value ?? '',
    unit: String(obj.unit ?? obj.units ?? '').trim(),
  };
}

function isRequiredChildMissing(child: ParameterDefinition, raw: unknown): boolean {
  const required = Boolean(child.required || (child as any).mandatory);
  if (!required) return false;

  const usesUnitPair = child.fieldType === 'units' || hasValidUnits(child.parameterTags);
  if (usesUnitPair) {
    const pair = readUnitAndAmount(raw);
    if (isEmptyScalar(pair.amount)) return true;
    if (!pair.unit) return true;
    return false;
  }

  return isEmptyScalar(raw);
}

// ---------------------------------------------------------------------------
// Child field renderer (recursive)
// ---------------------------------------------------------------------------

interface ChildFieldRendererProps {
  child: ParameterDefinition;
  value: unknown;
  onChange: (v: unknown) => void;
  uniquenessError?: string;
  uniquenessPending?: boolean;
}

/**
 * Renders a single child field inside a group row.
 * Handles text, textNumber, number, units, range, dropdown, date, and nested group.
 */
function ChildFieldRenderer({
  child,
  value,
  onChange,
  uniquenessError,
  uniquenessPending,
}: ChildFieldRendererProps) {
  const label = `${child.parameterName}${child.required ? ' *' : ''}`;
  const units = child.parameterTags ?? [];
  const validUnits = hasValidUnits(units);
  const hintText = uniquenessError ?? (uniquenessPending ? 'Checking uniqueness...' : '');

  const renderHint = () => {
    if (!hintText) return null;
    return (
      <Typography
        variant="caption"
        sx={{
          mt: 0.5,
          display: 'block',
          color: uniquenessError ? 'error.main' : 'text.secondary',
        }}
      >
        {hintText}
      </Typography>
    );
  };

  switch (child.fieldType) {
    case 'text':
    case 'textNumber':
    case 'number': {
      if (validUnits) {
        return (
          <Box>
            <UnitValueField
              label={label}
              value={toUnitValue(value, '')}
              units={units}
              onChange={(unitValue: UnitValue) => {
                if (child.fieldType === 'text') {
                  const validated = validateUnitValue(unitValue, 'text');
                  onChange(validated);
                } else {
                  onChange(unitValue);
                }
              }}
              inputType={child.fieldType === 'number' ? 'number' : 'text'}
            />
            {renderHint()}
          </Box>
        );
      }
      return (
        <TextField
          size="small"
          label={label}
          type={child.fieldType === 'number' ? 'number' : 'text'}
          placeholder={`Enter ${child.parameterName}`}
          fullWidth
          value={(() => {
            if (child.fieldType !== 'number') return String(extractPrimitive(value));
            const primitive = extractPrimitive(value);
            if (primitive === '') return '';
            const parsed = Number(primitive);
            return Number.isFinite(parsed) ? parsed : '';
          })()}
          onChange={(e) => {
            if (child.fieldType === 'number') {
              onChange(e.target.value === '' ? null : Number(e.target.value));
            } else if (child.fieldType === 'text') {
              const validated = validateParameterInput(e.target.value, 'text');
              onChange(validated);
            } else {
              onChange(e.target.value);
            }
          }}
          error={Boolean(uniquenessError)}
          helperText={hintText || undefined}
          slotProps={child.fieldType === 'number' ? { htmlInput: { step: 'any' } } : undefined}
        />
      );
    }

    case 'units':
      return (
        <Box>
          <UnitValueField
            label={label}
            value={toUnitValue(value, '')}
            units={units}
            onChange={onChange as (v: UnitValue) => void}
            inputType="text"
          />
          {renderHint()}
        </Box>
      );

    case 'range': {
      const min = child.rangeMin;
      const max = child.rangeMax;
      
      // Build label with range brackets for reference
      const rangeBrackets = (min !== null && min !== undefined && max !== null && max !== undefined)
        ? ` (${min} - ${max})`
        : '';
      const fullLabel = `${child.parameterName}${rangeBrackets}${child.required ? ' *' : ''}`;
      
      if (validUnits) {
        return (
          <UnitValueField
            label={fullLabel}
            value={toUnitValue(value, '')}
            units={units}
            inputType="number"
            onChange={onChange as (v: UnitValue) => void}
          />
        );
      }
      
      const currentVal = toRangeValue(value);
      return (
        <TextField
          size="small"
          label={fullLabel}
          type="number"
          placeholder={`Enter ${child.parameterName}`}
          fullWidth
          value={currentVal === '' ? '' : currentVal}
          onChange={(e) => {
            const validated = validateRangeInput(e.target.value, min, max);
            if (validated !== '') {
              onChange(validated);
            }
          }}
          error={Boolean(uniquenessError)}
          helperText={hintText || undefined}
          slotProps={{ htmlInput: { step: 'any' } }}
        />
      );
    }

    case 'dropdown':
      return (
        <Box>
          <DropdownField
            label={label}
            value={value}
            options={child.dropDownList ?? []}
            onChange={onChange as (v: string) => void}
          />
          {renderHint()}
        </Box>
      );

    case 'date':
      return (
        <TextField
          size="small"
          label={label}
          type="date"
          placeholder={`Enter ${child.parameterName}`}
          fullWidth
          value={String(extractPrimitive(value))}
          onChange={(e) => onChange(e.target.value)}
          error={Boolean(uniquenessError)}
          helperText={hintText || undefined}
          slotProps={{ inputLabel: { shrink: true } }}
        />
      );

    case 'group':
      // Nested groups are always rendered as single (no allowMultiple for nested level)
      return (
        <GroupField
          label={label}
          childDefinitions={child.children ?? []}
          value={(value as Record<string, unknown>) ?? {}}
          allowMultiple={false}
          onChange={onChange as (v: unknown) => void}
        />
      );

    default:
      return (
        <TextField
          size="small"
          label={label}
          placeholder={`Enter ${child.parameterName}`}
          fullWidth
          value={String(extractPrimitive(value))}
          onChange={(e) => onChange(e.target.value)}
          error={Boolean(uniquenessError)}
          helperText={hintText || undefined}
        />
      );
  }
}

// ---------------------------------------------------------------------------
// GroupField
// ---------------------------------------------------------------------------

interface GroupFieldSingleValue extends Record<string, unknown> {}
type GroupFieldValue = GroupFieldSingleValue | GroupFieldSingleValue[];

interface GroupFieldProps {
  label: string;
  /** The child field definitions (ParameterDefinition.children / Angular paramValues). */
  childDefinitions: ParameterDefinition[];
  value: GroupFieldValue | null;
  /** When true, shows Add/Remove row controls and treats value as an array. */
  allowMultiple?: boolean;
  onChange: (v: GroupFieldValue) => void;
  required?: boolean;
  pathPrefix?: string;
  focusedPath?: string | null;
  uniquenessErrors?: Record<string, string>;
  uniquenessPendingMap?: Record<string, boolean>;
}

/**
 * Reusable group parameter field.
 * Mirrors Angular's GROUP FIELD (single & multiple) sections from step-parameters.
 *
 * - `allowMultiple = false` → renders child fields once inside a bordered box.
 * - `allowMultiple = true`  → renders a list of row cards with Add / Remove buttons.
 *
 * Child fields are rendered recursively via `ChildFieldRenderer`, which supports
 * text, textNumber, number, units, range, dropdown, date, and nested group.
 */
export function GroupField({
  label,
  childDefinitions,
  value,
  allowMultiple = false,
  onChange,
  required = false,
  pathPrefix = '',
  focusedPath = null,
  uniquenessErrors = {},
  uniquenessPendingMap = {},
}: GroupFieldProps) {
  const focusedChildSx = {
    '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
      borderColor: '#ef4444 !important',
      borderWidth: '2px',
    },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#ef4444 !important',
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#ef4444 !important',
    },
  } as const;

  // ---- Single group (allowMultiple = false) --------------------------------

  if (!allowMultiple) {
    const singleValue = (value as Record<string, unknown>) ?? {};

    const updateField = (key: string, val: unknown) => {
      onChange({ ...singleValue, [key]: val });
    };

    return (
      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5, mb: 1 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'text.secondary' }}>
          {label}
          {required && <span style={{ color: 'red' }}> *</span>}
        </Typography>

        {childDefinitions.length === 0 && (
          <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic' }}>
            No child fields defined.
          </Typography>
        )}

        {[...childDefinitions]
          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
          .map((child) => {
          const key = childKey(child);
          const uniquePath = pathPrefix ? `${pathPrefix}.${key}` : key;
          // Try key, then fall back to parameterName for backwards compat
          const childValue = singleValue[key] ?? singleValue[child.parameterName];
          const isFocused = focusedPath === uniquePath;
          const shouldHighlight = isFocused && isRequiredChildMissing(child, childValue);
          return (
            <Box
              key={child.id || key}
              data-validation-path={uniquePath}
              sx={{ mb: 1.5, ...(shouldHighlight ? focusedChildSx : {}) }}
            >
              <ChildFieldRenderer
                child={child}
                value={childValue}
                onChange={(val) => updateField(key, val)}
                uniquenessError={uniquenessErrors[uniquePath]}
                uniquenessPending={Boolean(uniquenessPendingMap[uniquePath])}
              />
            </Box>
          );
        })}
      </Box>
    );
  }

  // ---- Multiple group (allowMultiple = true) --------------------------------

  // Ensure at least one empty row is visible by default
  const rawRows = (Array.isArray(value) ? value : []) as Record<string, unknown>[];
  const rows = rawRows.length === 0 ? [makeEmptyRowValue(childDefinitions)] : rawRows;

  const updateRowField = (rowIdx: number, key: string, val: unknown) => {
    const updated = rows.map((row, i) => (i === rowIdx ? { ...row, [key]: val } : row));
    onChange(updated);
  };

  const addRow = () => {
    onChange([...rows, makeEmptyRowValue(childDefinitions)]);
  };

  const removeRow = (rowIdx: number) => {
    if (rows.length > 1) {
      onChange(rows.filter((_, i) => i !== rowIdx));
    }
  };

  return (
    <Box sx={{ mb: 1 }}>
      {/* Header + Add button */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1,
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          {label}
          {required && <span style={{ color: 'red' }}> *</span>}
        </Typography>
        <Button
          size="small"
          startIcon={<AddIcon />}
          variant="outlined"
          onClick={addRow}
        >
          Add {label}
        </Button>
      </Box>

      {/* Row cards */}
      {rows.length === 0 && (
        <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic' }}>
          No entries yet.
        </Typography>
      )}

      {rows.map((rowValue, rowIdx) => (
        <Box
          key={rowIdx}
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            p: 1.5,
            mb: 1,
            position: 'relative',
          }}
        >
          {/* Remove button — top-right */}
          <Box sx={{ position: 'absolute', top: 4, right: 4 }}>
            <IconButton
              size="small"
              color="error"
              onClick={() => removeRow(rowIdx)}
              disabled={rows.length <= 1}
              title={rows.length <= 1 ? 'Cannot delete the only row' : `Remove ${label} row`}
            >
              <DeleteIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>

          {/* Child fields */}
          {childDefinitions.map((child) => {
            const key = childKey(child);
            const uniquePath = pathPrefix ? `${pathPrefix}[${rowIdx}].${key}` : `${key}[${rowIdx}]`;
            const childValue = rowValue[key] ?? rowValue[child.parameterName];
            const isFocused = focusedPath === uniquePath;
            const shouldHighlight = isFocused && isRequiredChildMissing(child, childValue);
            return (
              <Box
                key={child.id || key}
                data-validation-path={uniquePath}
                sx={{ mb: 1.5, pr: 4, ...(shouldHighlight ? focusedChildSx : {}) }}
              >
                <ChildFieldRenderer
                  child={child}
                  value={childValue}
                  onChange={(val) => updateRowField(rowIdx, key, val)}
                  uniquenessError={uniquenessErrors[uniquePath]}
                  uniquenessPending={Boolean(uniquenessPendingMap[uniquePath])}
                />
              </Box>
            );
          })}
        </Box>
      ))}
    </Box>
  );
}
